// src/utils/jobSheetPDFGenerator.js
// Exact PDF generation from admin JobViewer.js
// 
// ✨ UPDATED: Now supports optional End Customer fields for reseller jobs and accepts template and engineerName
// 
// How it works:
// - If job.endCustomerName exists → Shows end customer on job sheet
// - If job.endCustomerName is empty → Shows main customer (backward compatible)
// - Adds "Ordered by: [mainCustomer]" note when end customer is shown
//
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import jsPDF from "jspdf";
import { formatDate } from "./formatHelpers"; // Use centralized format helper

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/humbertack-workflow.firebasestorage.app/o/Minimalist%20HumberTrack%20Logo%20Design.png?alt=media&token=7fac08dd-27a3-466a-8172-48d50a827959";

// Helper: Convert URL to base64
const toDataURL = async (url) => {
  if (!url) return null;
  if (url.startsWith("data:image")) return url;

  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("toDataURL error", err);
    return null;
  }
};

// Helper: Get image info with optional landscape rotation
const getImageInfo = async (dataUrl, { rotateLandscape = true } = {}) => {
  if (!dataUrl) return null;
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const srcW = img.naturalWidth || img.width || 0;
      const srcH = img.naturalHeight || img.height || 0;

      if (!rotateLandscape) {
        return resolve({ dataUrl, width: srcW, height: srcH });
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (srcW > srcH) {
        canvas.width = srcH;
        canvas.height = srcW;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.drawImage(img, -srcW / 2, -srcH / 2);
      } else {
        canvas.width = srcW;
        canvas.height = srcH;
        ctx.drawImage(img, 0, 0);
      }

      const fixedDataUrl = canvas.toDataURL("image/jpeg", 0.9);
      resolve({
        dataUrl: fixedDataUrl,
        width: canvas.width,
        height: canvas.height,
      });
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
};

// Helper: Add image simple
const addImageSimple = (pdf, imgInfo, x, y, maxW, maxH) => {
  if (!imgInfo) return;
  const { dataUrl } = imgInfo;
  let { width, height } = imgInfo;
  if (!width || !height) {
    width = maxW;
    height = maxH;
  }
  const ratio = width / height;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  const format = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
  pdf.addImage(dataUrl, format, x, y, w, h);
};

// Helper: Add image in box
const addImageInBox = (pdf, imgInfo, x, y, maxW, maxH) => {
  if (!imgInfo) return 0;
  const { dataUrl } = imgInfo;
  let { width, height } = imgInfo;
  if (!width || !height) {
    width = maxW;
    height = maxH;
  }
  const ratio = width / height;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  const format = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
  pdf.addImage(dataUrl, format, x, y, w, h);
  return h;
};

// MAIN FUNCTION: Generate Job Sheet PDF (EXACT ADMIN FORMAT)
export const generateJobSheetPDF = async (job, template = null, engineerName = null) => {
  try {
    const pdf = new jsPDF("p", "mm", "a4");
    const margin = 10;
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    let y = 20;

    // If template is not provided, try to load it from Firestore
    if (!template && job.jobTemplateId) {
      try {
        const templateRef = doc(db, "jobSheetTemplates", job.jobTemplateId);
        const templateSnap = await getDoc(templateRef);
        if (templateSnap.exists()) {
          template = templateSnap.data();
        }
      } catch (err) {
        console.error("Error loading template for PDF:", err);
      }
    }

    // If engineerName is not provided, try to construct it from job data
    if (!engineerName && job.engineerName) {
      engineerName = job.engineerName;
    } else if (!engineerName && job.assignedEngineerName) {
      engineerName = job.assignedEngineerName;
    } else if (!engineerName && job.assignedEngineerEmail) {
      engineerName = job.assignedEngineerEmail;
    }

    // Extract fields - supports both new completionData and legacy formData formats
    let fields = [];
    
    if (job.completionData && typeof job.completionData === 'object' && !Array.isArray(job.completionData)) {
      if (template?.fields) {
        fields = template.fields.map(field => ({
          id: field.id,
          label: field.label,
          type: field.type,
          value: job.completionData[field.id] ?? null,
        }));
      } else {
        // Fallback: create fields from completionData keys
        fields = Object.entries(job.completionData).map(([key, value]) => ({
          id: key,
          label: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
          type: Array.isArray(value) ? 'photo' : typeof value === 'boolean' ? 'checkbox' : 'text',
          value: value,
        }));
      }
    }
    // Legacy formData format (array of submissions)
    else if (job.formData && Array.isArray(job.formData)) {
      const submissions = job.formData;
      const submission = submissions[submissions.length - 1];
      fields = submission?.fields || [];
    }

    const textFields = fields.filter(
      (f) => f.type !== "photo" && f.type !== "signature"
    );
    
    // Handle photo fields - expand arrays
    const imageFields = [];
    fields.forEach(f => {
      if (f.type === "photo" && f.value) {
        if (Array.isArray(f.value)) {
          f.value.forEach((url, idx) => {
            imageFields.push({
              ...f,
              id: `${f.id}_${idx}`,
              label: `${f.label} (${idx + 1})`,
              value: url,
            });
          });
        } else {
          imageFields.push(f);
        }
      } else if (f.type === "signature" && f.value) {
        imageFields.push(f);
      }
    });

    // Preload images
    const inlineImages = {};

    for (const f of imageFields) {
      const dataUrl = await toDataURL(f.value);
      const info = await getImageInfo(dataUrl, {
        rotateLandscape: f.type === "photo",
      });
      if (info) inlineImages[f.id] = info;
    }

    if (job.completionData?.signatureUrl) {
      const sigDataUrl = await toDataURL(job.completionData.signatureUrl);
      const sigInfo = await getImageInfo(sigDataUrl, {
        rotateLandscape: false,
      });
      if (sigInfo) inlineImages["completion_signature"] = sigInfo;
    }

    const logoDataUrl = await toDataURL(LOGO_URL);
    const logoInfo = await getImageInfo(logoDataUrl, {
      rotateLandscape: false,
    });

    // Draw header function
    const drawHeader = () => {
      const headerHeight = 22;
      pdf.setFillColor(10, 31, 59);
      pdf.rect(0, 0, pageW, headerHeight, "F");

      if (logoInfo) {
        addImageSimple(pdf, logoInfo, margin, (headerHeight - 10) / 2, 40, 10);
      } else {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(14);
        pdf.setTextColor(255);
        pdf.text("HumberTrack", margin, 15);
      }

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(255);

      const rightLines = [
        "Installer Details - Humbertrack Ltd",
        "34 Plantation Drive",
        "North Ferriby, HU143BE",
        "Tel. 07897 201067  Email. installs@humbertrack.co.uk",
      ];

      const lineHeight = 3.5;
      const startY =
        (headerHeight - rightLines.length * lineHeight) / 2 + lineHeight;

      rightLines.forEach((line, i) =>
        pdf.text(line, pageW - margin, startY + i * lineHeight, {
          align: "right",
        })
      );

      const titleY = headerHeight + 10;
      pdf.setTextColor(0);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.text("HumberTrack Job Sheet", pageW / 2, titleY, {
        align: "center",
      });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text(
        `Job #${job.jobReferenceNumber || job.id}`,
        pageW / 2,
        titleY + 5,
        {
          align: "center",
        }
      );

      y = titleY + 12;
      pdf.setDrawColor(200);
      pdf.line(margin, y, pageW - margin, y);
      y += 6;
    };

    const ensureSpace = (needed) => {
      if (y + needed > pageH - 20) {
        pdf.addPage();
        drawHeader();
      }
    };

    drawHeader();

    // Determine which customer details to show
    // If end customer details exist, show them; otherwise show main customer
    const displayCustomerName = job.endCustomerName || job.customerName || "";
    const displayCustomerAddress = job.endCustomerAddress || job.siteAddress || "";
    const displayCustomerContact = job.endCustomerContact || job.siteContact || "";
    const displayCustomerPhone = job.endCustomerPhone || "";
    const isEndCustomer = !!job.endCustomerName; // Flag to show "Ordered by" note

    // Customer / Engineer block
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("Customer", margin, y);
    pdf.text("Engineer", pageW / 2, y);
    y += 6;

    pdf.setFont("helvetica", "normal");
    pdf.text(displayCustomerName, margin, y);
    pdf.text(engineerName || job.engineerName || "", pageW / 2, y);
    y += 5;

    // Show address (end customer address or site address)
    if (displayCustomerAddress) {
      const addrLines = pdf.splitTextToSize(
        displayCustomerAddress,
        pageW / 2 - margin * 1.5
      );
      addrLines.forEach((line) => {
        pdf.text(line, margin, y);
        y += 4;
      });
    }

    // Show contact info
    const contactText = displayCustomerContact
      ? `Contact: ${displayCustomerContact}`
      : "";
    
    if (contactText) {
      pdf.text(contactText, margin, y);
      y += 4;
    }
    
    if (displayCustomerPhone) {
      pdf.text(`Phone: ${displayCustomerPhone}`, margin, y);
      y += 4;
    }

    // If this is for an end customer, show who ordered it (reseller)
    if (isEndCustomer && job.customerName) {
      pdf.setFontSize(8);
      pdf.setTextColor(100);
      pdf.text(`Ordered by: ${job.customerName}`, margin, y);
      pdf.setTextColor(0);
      pdf.setFontSize(9);
      y += 4;
    }

    y += 4;

    pdf.setFont("helvetica", "bold");
    pdf.text("Job type", margin, y);
    pdf.text("Reference", pageW / 2, y);
    y += 5;

    pdf.setFont("helvetica", "normal");
    pdf.text(job.jobTemplateName || "", margin, y);
    pdf.text(String(job.jobReferenceNumber || job.id), pageW / 2, y);
    y += 5;

    pdf.text("Vehicle: " + (job.vehicleReg || ""), margin, y);
    y += 5;
    pdf.text(
      `Created: ${formatDate(job.createdAt)}   Completed: ${formatDate(
        job.completionData?.completedAt
      )}`,
      margin,
      y
    );
    y += 8;

    pdf.setDrawColor(200);
    pdf.line(margin, y, pageW - margin, y);
    y += 6;

    // Q&A table
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("Job Sheet - Questions & Answers", margin, y);
    y += 6;

    pdf.setDrawColor(150);
    pdf.setFillColor(230, 230, 230);
    const labelW = 70;
    const headerH = 7;
    pdf.rect(margin, y - 5, labelW, headerH, "FD");
    pdf.rect(
      margin + labelW,
      y - 5,
      pageW - margin * 2 - labelW,
      headerH,
      "FD"
    );
    pdf.setFontSize(9);
    pdf.setTextColor(0);
    pdf.text("Question", margin + 2, y - 1.5);
    pdf.text("Answer", margin + labelW + 2, y - 1.5);
    y += 4;

    const valueW = pageW - margin * 2 - labelW;
    pdf.setDrawColor(210);

    let zebra = false;

    for (const field of textFields) {
      if (field.type === "section") {
        const secText = field.label || "";
        const secLines = pdf.splitTextToSize(secText, labelW + valueW - 4);
        const secHeight = secLines.length * 4 + 3;

        ensureSpace(secHeight + 4);

        pdf.setFillColor(230, 240, 255);
        pdf.rect(margin, y, labelW + valueW, secHeight, "F");
        pdf.setDrawColor(190);
        pdf.rect(margin, y, labelW + valueW, secHeight);

        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(20, 40, 80);
        pdf.text(secLines, margin + 2, y + 4);

        pdf.setTextColor(0);
        y += secHeight + 2;

        zebra = false;
        continue;
      }

      const labelLines = pdf.splitTextToSize(field.label, labelW - 3);
      const val =
        field.value == null || field.value === ""
          ? "N/A"
          : String(field.value);
      const valueLines = pdf.splitTextToSize(val, valueW - 3);
      const rowH = 4 * Math.max(labelLines.length, valueLines.length) + 3;

      ensureSpace(rowH + 4);

      if (zebra) {
        pdf.setFillColor(245, 245, 245);
        pdf.rect(margin, y, labelW + valueW, rowH, "F");
      }
      zebra = !zebra;

      pdf.setFont("helvetica", "bold");
      pdf.text(labelLines, margin + 2, y + 4);
      pdf.setFont("helvetica", "normal");
      pdf.text(valueLines, margin + labelW + 2, y + 4);

      pdf.rect(margin, y, labelW, rowH);
      pdf.rect(margin + labelW, y, valueW, rowH);

      y += rowH;
    }

    // Photos page
    if (imageFields.length > 0) {
      pdf.addPage();
      drawHeader();

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("Photos", margin, y);
      y += 6;

      const gap = 6;
      const colWidth = (pageW - margin * 2 - gap) / 2;
      const imgMaxW = colWidth - 4;
      const imgMaxH = 45;

      let colIndex = 0;
      let rowStartY = y;

      for (const field of imageFields) {
        const imgInfo = inlineImages[field.id];
        if (!imgInfo) continue;

        const x = colIndex === 0 ? margin : margin + colWidth + gap;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        const labelLines = pdf.splitTextToSize(field.label, imgMaxW);
        const labelHeight = labelLines.length * 4;

        ensureSpace(labelHeight + imgMaxH + 12);

        pdf.text(labelLines, x, y);
        const imgY = y + labelHeight + 2;
        const usedH = addImageInBox(pdf, imgInfo, x, imgY, imgMaxW, imgMaxH);
        const cellHeight = labelHeight + 2 + usedH + 8;

        if (colIndex === 0) {
          rowStartY = y;
          colIndex = 1;
        } else {
          y = rowStartY + cellHeight;
          colIndex = 0;
        }

        if (
          field === imageFields[imageFields.length - 1] &&
          colIndex === 1
        ) {
          y = rowStartY + cellHeight;
          colIndex = 0;
        }

        if (y > pageH - 30 && field !== imageFields[imageFields.length - 1]) {
          pdf.addPage();
          drawHeader();
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(11);
          pdf.text("Photos (cont.)", margin, 24 + 6);
          y = 24 + 12;
          rowStartY = y;
          colIndex = 0;
        }
      }
    }

    // Customer sign-off
    pdf.addPage();
    drawHeader();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("Customer Sign-off", margin, y);
    y += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(
      "Customer Name: " + (job.completionData?.customerName || "N/A"),
      margin,
      y
    );
    y += 8;

    const custSig = inlineImages["completion_signature"];
    if (custSig) {
      pdf.text("Signature:", margin, y);
      y += 4;
      addImageSimple(pdf, custSig, margin, y, 60, 25);
      y += 32;
    } else {
      pdf.text("No signature on record.", margin, y);
      y += 8;
    }

    // Footer
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      const footerY = pageH - 8;
      pdf.setFontSize(8);
      pdf.setDrawColor(200);
      pdf.line(margin, footerY - 4, pageW - margin, footerY - 4);
      pdf.text(
        "Humbertrack Ltd   |   humbertrack.co.uk   |   07897 201067",
        margin,
        footerY
      );
      pdf.text(`Page ${i} of ${pageCount}`, pageW - margin, footerY, {
        align: "right",
      });
    }

    pdf.save(`job-sheet-${job.jobReferenceNumber || job.id}.pdf`);
    return { success: true };
  } catch (err) {
    console.error("PDF generation error", err);
    return { success: false, error: err.message };
  }
};
